// Curated exercise library — ~150 movements spanning 13 muscle groups, 8 equipment types
// and 3 difficulty tiers. Each entry is consumed by /app/library and is also the
// reference set for the AI workout generator's vocabulary.
const ex = (name, muscle_group, equipment, difficulty, is_bodyweight, is_static_hold, steps, tip) => ({
  name, muscle_group, equipment, difficulty, is_bodyweight, is_static_hold, steps, tip,
});

export const exerciseLibrary = [
  /* ============================ CHEST ============================ */
  ex("Barbell Bench Press", "chest", "barbell", "intermediate", false, false,
    ["Lie on a flat bench, eyes under the bar.",
     "Grip slightly wider than shoulders; unrack and lower bar to mid-chest.",
     "Drive the bar up explosively; lock elbows at the top.",
     "Keep shoulder blades pinched together throughout."],
    "Plant your feet firmly and keep a slight arch in your lower back to protect the shoulders."),
  ex("Incline Barbell Bench Press", "chest", "barbell", "intermediate", false, false,
    ["Set bench to 30–45° and lie back, eyes under the bar.",
     "Unrack and lower to your upper chest with control.",
     "Press up powerfully, locking out without flaring elbows."],
    "Lower the bench angle if you feel the work shifting to your front delts."),
  ex("Decline Barbell Bench Press", "chest", "barbell", "intermediate", false, false,
    ["Lock feet into a decline bench, eyes under the bar.",
     "Lower the bar to your lower pec with a tight back.",
     "Press to lockout, keeping the bar path slightly forward."],
    "Great for emphasising the lower portion of the pec."),
  ex("Push-Up", "chest", "bodyweight", "beginner", true, false,
    ["Start in a high plank, hands under shoulders.",
     "Lower your chest to within an inch of the floor with elbows ~45°.",
     "Push back to start, fully locking out the elbows."],
    "Squeeze your glutes and brace your core — no sagging hips."),
  ex("Incline Push-Up", "chest", "bodyweight", "beginner", true, false,
    ["Place hands on a bench or box.",
     "Lower chest to the bench with elbows ~45°.",
     "Press back to lockout."],
    "Easier scaling of the push-up while still hitting the upper chest."),
  ex("Decline Push-Up", "chest", "bodyweight", "intermediate", true, false,
    ["Feet elevated on a bench, hands on the floor.",
     "Lower your chest to the floor under control.",
     "Press back to a full lockout."],
    "Shifts the load toward the upper chest and front delts."),
  ex("Diamond Push-Up", "chest", "bodyweight", "intermediate", true, false,
    ["Form a diamond with index fingers and thumbs under your sternum.",
     "Lower your chest to the diamond with elbows tucked.",
     "Press up explosively."],
    "Bias toward triceps and inner chest — keep elbows close."),
  ex("Incline Dumbbell Press", "chest", "dumbbells", "intermediate", false, false,
    ["Set bench to 30–45°.",
     "Press dumbbells from shoulder height to overhead with palms forward.",
     "Lower slowly under control to the start position."],
    "Don't flare elbows past 75° — protects the front delts."),
  ex("Flat Dumbbell Press", "chest", "dumbbells", "intermediate", false, false,
    ["Lie on a flat bench with a dumbbell in each hand at chest level.",
     "Press the weights up until arms are locked out.",
     "Lower slowly back to the start."],
    "Stop just short of touching the dumbbells — keep tension on the chest."),
  ex("Dumbbell Fly", "chest", "dumbbells", "intermediate", false, false,
    ["Lie flat with dumbbells above your chest, palms facing in.",
     "With a slight elbow bend, lower the weights out in an arc to chest level.",
     "Squeeze the chest to bring the dumbbells back together."],
    "Imagine hugging a giant tree — the elbow angle never changes."),
  ex("Cable Crossover", "chest", "cables", "intermediate", false, false,
    ["Set cables to upper-pulley, one handle each side.",
     "Step forward with a soft elbow bend.",
     "Pull both handles down and across until they meet in front of your hips."],
    "Cross the hands slightly past midline at the end for a deeper squeeze."),
  ex("Machine Chest Press", "chest", "machines", "beginner", false, false,
    ["Adjust the seat so the handles align with your sternum.",
     "Press until elbows lock out without flaring.",
     "Lower under control."],
    "Great option to learn the pressing pattern with a stable load."),
  ex("Pec Deck", "chest", "machines", "beginner", false, false,
    ["Sit with your back flat against the pad, forearms on the handles.",
     "Squeeze your chest to bring the pads together in front.",
     "Resist on the way back."],
    "Keep shoulders down and back to prevent front-delt takeover."),
  ex("Resistance Band Chest Press", "chest", "resistance bands", "beginner", false, false,
    ["Anchor a band at chest height behind you, hold a handle in each hand.",
     "Press the handles forward until your arms are extended.",
     "Return slowly to the start."],
    "Step further from the anchor to add tension."),
  ex("Svend Press", "chest", "dumbbells", "beginner", false, false,
    ["Hold a single plate (or dumbbell) between your palms at chest height.",
     "Press it forward while squeezing your hands together.",
     "Pull back to the chest with control."],
    "Constant inward pressure on the palms keeps the chest under tension."),

  /* ============================ BACK ============================ */
  ex("Pull-Up", "back", "pull-up bar", "intermediate", true, false,
    ["Hang from the bar with hands shoulder-width apart, palms forward.",
     "Pull yourself up until your chin clears the bar.",
     "Lower under control with arms fully extended."],
    "Imagine driving your elbows into your back pockets to engage lats."),
  ex("Wide-Grip Pull-Up", "back", "pull-up bar", "advanced", true, false,
    ["Grip the bar wider than shoulder-width, palms forward.",
     "Pull yourself up keeping elbows wide.",
     "Lower with control to a full dead hang."],
    "Greater lat width emphasis but tougher on the shoulders — earn the range."),
  ex("Bent-Over Row", "back", "barbell", "intermediate", false, false,
    ["Hinge at hips with a flat back, knees soft.",
     "Pull the bar to your lower ribcage, elbows tucked.",
     "Lower with control."],
    "Keep your neck neutral — look ~6 ft in front of you."),
  ex("Pendlay Row", "back", "barbell", "advanced", false, false,
    ["Set the bar on the floor with a hip hinge, back parallel to the ground.",
     "Explosively row the bar to your lower chest.",
     "Lower fully to the floor between every rep."],
    "Resetting every rep keeps the back tight and removes momentum."),
  ex("T-Bar Row", "back", "machines", "intermediate", false, false,
    ["Straddle the T-bar with feet planted, hinge with a flat back.",
     "Pull the handles to your lower chest, elbows tucked.",
     "Lower under control."],
    "Drive elbows back rather than pulling with the hands."),
  ex("Seated Cable Row", "back", "cables", "beginner", false, false,
    ["Sit upright with feet braced, grip the V-handle with arms extended.",
     "Pull the handle to your belly button, squeezing shoulder blades.",
     "Return slowly to a full stretch."],
    "Avoid leaning back beyond vertical — let the lats do the work."),
  ex("Lat Pulldown", "back", "cables", "beginner", false, false,
    ["Sit with thighs locked under the pad, grip just outside shoulders.",
     "Pull the bar to your upper chest with elbows down and back.",
     "Return slowly to a full stretch."],
    "Lead with the elbows, not the hands."),
  ex("Single-Arm Dumbbell Row", "back", "dumbbells", "beginner", false, false,
    ["Place one knee and hand on a bench for support.",
     "Row the dumbbell to your hip with a tucked elbow.",
     "Lower fully to a stretch."],
    "Drive your elbow toward the ceiling, not behind you."),
  ex("Inverted Row", "back", "bodyweight", "beginner", true, false,
    ["Set a bar at hip height; hang under it with a straight body.",
     "Pull your chest to the bar, squeezing shoulder blades.",
     "Lower slowly to full extension."],
    "Lower the bar to make it harder; raise it to scale down."),
  ex("Conventional Deadlift", "back", "barbell", "advanced", false, false,
    ["Stand with feet hip-width, bar over mid-foot.",
     "Hinge and grip just outside knees with a flat back.",
     "Drive through the floor to stand tall, then lower with control."],
    "Pull the slack out of the bar before liftoff — no jerking."),
  ex("Dead-Hang", "back", "pull-up bar", "beginner", true, true,
    ["Grip a pull-up bar shoulder-width, palms forward.",
     "Hang fully with shoulders engaged (slightly pulled down).",
     "Hold for prescribed time."],
    "Great for grip strength and decompressing the spine."),
  ex("Renegade Row", "back", "dumbbells", "intermediate", false, false,
    ["Start in a plank with hands on dumbbells.",
     "Row one dumbbell to your hip while staying square.",
     "Alternate sides without rocking the hips."],
    "Set your feet wide for stability if rotation creeps in."),
  ex("Face Pull", "back", "cables", "beginner", false, false,
    ["Set the rope at upper-chest height, step back to tension.",
     "Pull the rope toward your eyes, splitting the ends wide.",
     "Reverse slowly without losing posture."],
    "Lead with elbows high and wide — bulletproof those rear delts."),
  ex("Trap Bar Deadlift", "back", "barbell", "intermediate", false, false,
    ["Step inside the trap bar, hinge and grip the handles.",
     "Drive through the floor to stand tall.",
     "Lower under control with a flat back."],
    "More upright torso than a conventional deadlift — easier on the lower back."),
  ex("Rack Pull", "back", "barbell", "intermediate", false, false,
    ["Set the bar in the rack at knee height.",
     "Hinge with flat back, grip the bar.",
     "Stand tall by extending hips, then lower to the rack."],
    "Lets you overload the lockout without the floor pull."),
  ex("Banded Row", "back", "resistance bands", "beginner", false, false,
    ["Anchor a band at chest height; hold one handle each hand.",
     "Step back to tension and row both handles to your ribs.",
     "Return slowly to the start."],
    "Drive elbows behind your torso and squeeze the shoulder blades."),
  ex("Chest-Supported Row", "back", "dumbbells", "beginner", false, false,
    ["Lie chest-down on an incline bench, dumbbells hanging.",
     "Row the dumbbells to your hips with tucked elbows.",
     "Lower to a full stretch."],
    "Removes lower-back fatigue so you can hammer the upper back."),
  ex("Good Morning", "back", "barbell", "intermediate", false, false,
    ["Bar across upper back, knees soft.",
     "Hinge at the hips while keeping the back flat until torso is ~parallel.",
     "Drive hips forward to stand."],
    "Start light — this hammers the posterior chain and lower back."),

  /* ============================ SHOULDERS ============================ */
  ex("Overhead Press", "shoulders", "barbell", "intermediate", false, false,
    ["Stand with the bar at your collarbone, shoulder-width grip.",
     "Press the bar straight overhead, head moves through at lockout.",
     "Lower under control to the start."],
    "Brace your abs and squeeze glutes — no leaning back."),
  ex("Push Press", "shoulders", "barbell", "advanced", false, false,
    ["Bar at the front rack, knees softly bent.",
     "Dip and drive with the legs to launch the bar overhead.",
     "Lock out with arms vertical."],
    "Use the legs only for momentum — finish with the shoulders."),
  ex("Seated Dumbbell Press", "shoulders", "dumbbells", "intermediate", false, false,
    ["Sit upright with dumbbells at shoulder height.",
     "Press both overhead, palms forward.",
     "Lower under control."],
    "Set the bench just shy of vertical to support the lower back."),
  ex("Arnold Press", "shoulders", "dumbbells", "intermediate", false, false,
    ["Start with palms facing you at shoulder height.",
     "As you press, rotate the palms to face forward at the top.",
     "Reverse on the way down."],
    "Rotation hits all three deltoid heads in one rep."),
  ex("Lateral Raise", "shoulders", "dumbbells", "beginner", false, false,
    ["Hold dumbbells at your sides, slight bend in elbows.",
     "Raise to shoulder height with pinkies slightly up.",
     "Lower slowly over 2 seconds."],
    "Lead with the elbows, not the hands."),
  ex("Cable Lateral Raise", "shoulders", "cables", "beginner", false, false,
    ["Stand sideways to a low cable, grab the handle with the outside hand.",
     "Raise the arm out to shoulder height.",
     "Lower with control."],
    "Constant tension throughout the range — a side delt favourite."),
  ex("Front Raise", "shoulders", "dumbbells", "beginner", false, false,
    ["Hold dumbbells in front of your thighs, palms down.",
     "Raise one (or both) to shoulder height with a soft elbow.",
     "Lower under control."],
    "Stop at eye level — no need to go higher."),
  ex("Rear Delt Fly", "shoulders", "dumbbells", "beginner", false, false,
    ["Hinge forward with dumbbells hanging.",
     "Raise the weights out to the sides, leading with the elbows.",
     "Squeeze the rear delts at the top."],
    "Light weight, perfect form — rear delts respond to volume, not load."),
  ex("Upright Row", "shoulders", "barbell", "intermediate", false, false,
    ["Hold the bar in front of your thighs, shoulder-width grip.",
     "Pull the bar up to mid-chest, elbows leading.",
     "Lower with control."],
    "Stop when elbows reach shoulder height to protect the rotator cuff."),
  ex("Pike Push-Up", "shoulders", "bodyweight", "intermediate", true, false,
    ["Form an inverted V — hips high, hands shoulder-width.",
     "Lower the crown of your head toward the floor.",
     "Press back to start."],
    "Elevate feet on a bench to bias more shoulder load."),
  ex("Handstand Hold", "shoulders", "bodyweight", "advanced", true, true,
    ["Kick up to a handstand against a wall.",
     "Stack hips over shoulders over hands.",
     "Hold for prescribed time, breathing steadily."],
    "Press the floor away — long arms shrug the shoulders up."),
  ex("Landmine Press", "shoulders", "barbell", "intermediate", false, false,
    ["One end of a barbell in a landmine, the other at your shoulder.",
     "Press the bar forward and up to a near-lockout.",
     "Lower under control."],
    "Easier on the shoulders than vertical pressing if you lack overhead mobility."),
  ex("Banded Pull-Apart", "shoulders", "resistance bands", "beginner", false, false,
    ["Hold a band straight in front of your chest, hands wider than shoulders.",
     "Pull your hands apart until the band touches your chest.",
     "Return slowly."],
    "Killer warmup for the rear delts and rotator cuff."),
  ex("Machine Shoulder Press", "shoulders", "machines", "beginner", false, false,
    ["Adjust the seat so handles sit at shoulder height.",
     "Press to lockout, head stays neutral.",
     "Lower under control."],
    "Lets you push for fatigue without worrying about balance."),
  ex("Kettlebell Halo", "shoulders", "kettlebell", "beginner", false, false,
    ["Hold a kettlebell upside down at chest height.",
     "Circle it around your head, keeping the elbows close.",
     "Reverse direction every set."],
    "Slow it down — the goal is shoulder stability, not speed."),

  /* ============================ BICEPS ============================ */
  ex("Dumbbell Curl", "biceps", "dumbbells", "beginner", false, false,
    ["Stand with dumbbells at your sides, palms forward.",
     "Curl one (or both) up to shoulder height.",
     "Lower under control."],
    "Keep your elbows pinned to your sides — no swinging."),
  ex("Barbell Curl", "biceps", "barbell", "beginner", false, false,
    ["Hold a barbell with a shoulder-width underhand grip.",
     "Curl the bar to your chest, elbows pinned.",
     "Lower with control to full extension."],
    "Slight lean back is okay; rocking the torso is cheating."),
  ex("Hammer Curl", "biceps", "dumbbells", "beginner", false, false,
    ["Hold dumbbells with a neutral grip (palms facing in).",
     "Curl them to shoulder height while keeping palms facing each other.",
     "Lower under control."],
    "Targets the brachialis — adds thickness to the arm."),
  ex("Chin-Up", "biceps", "pull-up bar", "intermediate", true, false,
    ["Hang with hands shoulder-width, palms facing you.",
     "Pull yourself up until your chin clears the bar.",
     "Lower fully under control."],
    "Hits biceps harder than a pull-up because of the supinated grip."),
  ex("Incline Dumbbell Curl", "biceps", "dumbbells", "intermediate", false, false,
    ["Sit back on an incline bench, dumbbells hanging straight down.",
     "Curl both up, keeping elbows behind your torso.",
     "Lower fully."],
    "Maximal stretch on the long head — bicep peak builder."),
  ex("Preacher Curl", "biceps", "barbell", "intermediate", false, false,
    ["Rest the back of your arms on the preacher pad.",
     "Curl the bar with control.",
     "Lower until elbows are almost — but not fully — locked."],
    "Don't bottom out; preserves tension and protects the elbows."),
  ex("Cable Curl", "biceps", "cables", "beginner", false, false,
    ["Stand facing a low pulley with a straight bar.",
     "Curl with elbows pinned to your sides.",
     "Lower with a 2-second negative."],
    "Constant tension — perfect for an end-of-session finisher."),
  ex("Concentration Curl", "biceps", "dumbbells", "beginner", false, false,
    ["Sit on a bench, lean forward, elbow against the inner thigh.",
     "Curl one dumbbell to shoulder, keeping the elbow planted.",
     "Lower slowly."],
    "Squeeze the bicep hard at the top for max contraction."),
  ex("Banded Curl", "biceps", "resistance bands", "beginner", false, false,
    ["Stand on the band, hold a handle in each hand.",
     "Curl with elbows pinned.",
     "Lower with control."],
    "Tension increases at the top — natural overload."),
  ex("Spider Curl", "biceps", "dumbbells", "intermediate", false, false,
    ["Lie chest-down on an incline bench, dumbbells hanging.",
     "Curl them to shoulder height, elbows pointing down.",
     "Lower fully."],
    "Removes all body english — pure bicep work."),

  /* ============================ TRICEPS ============================ */
  ex("Triceps Dips", "triceps", "bodyweight", "intermediate", true, false,
    ["Support yourself on parallel bars (or a sturdy bench).",
     "Lower your body until elbows reach ~90°.",
     "Press back to lockout."],
    "Keep torso upright for triceps focus; lean forward for more chest."),
  ex("Bench Dip", "triceps", "bodyweight", "beginner", true, false,
    ["Hands on a bench behind you, legs out front.",
     "Lower until elbows reach 90°, keeping shoulders down.",
     "Press back to lockout."],
    "Walk feet further out to make it harder."),
  ex("Skull Crushers", "triceps", "barbell", "intermediate", false, false,
    ["Lie on a bench holding an EZ bar over your forehead.",
     "Bend elbows to lower the bar toward your forehead.",
     "Extend back up by squeezing the triceps."],
    "Keep upper arms vertical — only the forearms move."),
  ex("Triceps Pushdown", "triceps", "cables", "beginner", false, false,
    ["Stand facing a high cable, grip a straight bar or rope.",
     "Press down to lockout, elbows pinned.",
     "Return slowly to ~90°."],
    "Pause and squeeze for 1 second at lockout."),
  ex("Overhead Cable Extension", "triceps", "cables", "intermediate", false, false,
    ["Face away from a low cable, rope behind your head.",
     "Extend your arms overhead until elbows lock.",
     "Lower slowly to a deep stretch."],
    "Stretches the long head — perfect long-head builder."),
  ex("Dumbbell Overhead Extension", "triceps", "dumbbells", "beginner", false, false,
    ["Hold one dumbbell with both hands overhead.",
     "Lower behind your head with bent elbows.",
     "Press back to lockout."],
    "Keep elbows narrow — pointed up, not flaring out."),
  ex("Close-Grip Bench Press", "triceps", "barbell", "intermediate", false, false,
    ["Lie on a bench, grip the bar shoulder-width or slightly narrower.",
     "Lower with elbows tucked at 30°.",
     "Press to lockout, driving with the triceps."],
    "Wrist-shoulder-elbow stay in a straight line — no inward bend."),
  ex("Tate Press", "triceps", "dumbbells", "intermediate", false, false,
    ["Lie on a bench with dumbbells over your chest, palms out.",
     "Lower the weights to your sternum with elbows flaring.",
     "Press back, focusing on the triceps."],
    "Underrated movement for the medial head."),
  ex("Banded Triceps Pushdown", "triceps", "resistance bands", "beginner", false, false,
    ["Anchor a band overhead, grip the ends.",
     "Press the hands down to lockout, elbows pinned.",
     "Return slowly."],
    "Travel-friendly variation when no cables are available."),
  ex("Diamond Push-Up Hold", "triceps", "bodyweight", "intermediate", true, true,
    ["Form a diamond shape with the hands under your chest.",
     "Lower halfway down so the triceps are fully loaded.",
     "Hold the bottom for prescribed time."],
    "Quality isometric for sticking-point strength."),

  /* ============================ FOREARMS ============================ */
  ex("Farmer's Carry", "forearms", "dumbbells", "intermediate", false, false,
    ["Pick up a heavy dumbbell in each hand.",
     "Walk a fixed distance with shoulders pulled back.",
     "Set down with control."],
    "Grip is the bottleneck — train it with longer distances over time."),
  ex("Wrist Curl", "forearms", "dumbbells", "beginner", false, false,
    ["Sit with forearms resting on your thighs, palms up.",
     "Let the dumbbells roll to your fingertips.",
     "Curl your wrist up and squeeze."],
    "Slow and controlled — wrists are fragile."),
  ex("Reverse Wrist Curl", "forearms", "dumbbells", "beginner", false, false,
    ["Sit with forearms on your thighs, palms down.",
     "Lower the dumbbells under control.",
     "Extend the wrists back to neutral."],
    "Tiny range — focus on a strong squeeze at the top."),
  ex("Plate Pinch Hold", "forearms", "barbell", "intermediate", false, true,
    ["Pinch two smooth plates together with one hand.",
     "Hold them at your side for the prescribed time.",
     "Switch hands."],
    "Excellent open-hand strength builder."),
  ex("Wrist Roller", "forearms", "barbell", "intermediate", false, false,
    ["Hold the roller out in front with a weight hanging on a rope.",
     "Roll the weight up by alternating wrist flexion.",
     "Lower the weight by reversing the motion."],
    "Don't bend the elbows — keep them pinned at chest height."),
  ex("Dead Hang for Grip", "forearms", "pull-up bar", "beginner", true, true,
    ["Hang from a thick or normal bar.",
     "Engage the shoulders gently.",
     "Hold for prescribed time."],
    "Use a fat-grip attachment to multiply the grip stimulus."),

  /* ============================ CORE ============================ */
  ex("Plank", "core", "bodyweight", "beginner", true, true,
    ["Forearms on the floor, elbows under shoulders.",
     "Body in a straight line — head to heels.",
     "Brace the core and hold."],
    "Squeeze glutes hard; this protects the lower back."),
  ex("Side Plank", "core", "bodyweight", "beginner", true, true,
    ["Lie on your side, forearm under shoulder, feet stacked.",
     "Lift your hips into a straight line.",
     "Hold both sides equally."],
    "Drive the bottom hip up so the body forms a hard diagonal line."),
  ex("Hollow Body Hold", "core", "bodyweight", "intermediate", true, true,
    ["Lie on your back, arms overhead, legs straight.",
     "Press low back into the floor and lift arms and legs slightly off the floor.",
     "Hold while breathing."],
    "If the lower back arches, bend the knees until you can press the back flat."),
  ex("Hanging Leg Raise", "core", "pull-up bar", "advanced", true, false,
    ["Hang from a bar with a tight grip.",
     "Lift your legs to 90° (or higher) with control.",
     "Lower without swinging."],
    "Bend the knees to scale this down."),
  ex("Hanging Knee Raise", "core", "pull-up bar", "intermediate", true, false,
    ["Hang from a bar.",
     "Drive knees up to your chest with control.",
     "Lower fully to a dead hang."],
    "Avoid swinging — pause briefly at the top."),
  ex("Russian Twist", "core", "bodyweight", "beginner", true, false,
    ["Sit with knees bent, lean back ~45°.",
     "Rotate torso side to side, touching the floor each side.",
     "Keep feet hovering for more challenge."],
    "Drive the rotation from your obliques, not your arms."),
  ex("Dead Bug", "core", "bodyweight", "beginner", true, false,
    ["Lie on your back, arms overhead, knees bent at 90°.",
     "Lower opposite arm and leg to the floor.",
     "Return and switch sides."],
    "Lower back stays pressed into the floor the whole time."),
  ex("Bird Dog", "core", "bodyweight", "beginner", true, false,
    ["Start on hands and knees, hands under shoulders.",
     "Extend opposite arm and leg straight out.",
     "Return and switch."],
    "Pretend a glass of water is balanced on your back — no tipping."),
  ex("Pallof Press", "core", "cables", "beginner", false, false,
    ["Stand sideways to a cable at chest height, hands clasped on the handle.",
     "Press the handle straight out from your chest and resist rotation.",
     "Return slowly."],
    "Anti-rotation is harder than it looks — feet planted, ribs down."),
  ex("Ab Wheel Rollout", "core", "machines", "advanced", false, false,
    ["Kneel with a wheel under your shoulders.",
     "Roll forward, body forming a straight line.",
     "Return by squeezing the abs back to start."],
    "Tuck the pelvis — never let the lower back sag."),
  ex("Cable Crunch", "core", "cables", "intermediate", false, false,
    ["Kneel facing a high cable, rope at the back of your head.",
     "Crunch your elbows toward your knees.",
     "Return slowly without using the hips."],
    "Round the spine forward — abs flex, hips stay still."),
  ex("Toes-to-Bar", "core", "pull-up bar", "advanced", true, false,
    ["Hang from the bar.",
     "Drive your toes up to touch the bar.",
     "Lower with control."],
    "Bend the knees on the way down to control momentum."),
  ex("V-Up", "core", "bodyweight", "intermediate", true, false,
    ["Lie flat, arms and legs straight.",
     "Lift arms and legs to meet over your hips in a V shape.",
     "Lower under control."],
    "Strong exhale on the way up makes the contraction stronger."),
  ex("Mountain Climber", "core", "bodyweight", "beginner", true, false,
    ["Start in a high plank.",
     "Drive one knee toward your chest, then alternate quickly.",
     "Keep hips low and core braced."],
    "Set a steady tempo — slipping into a hip pop ruins the abs work."),
  ex("Bicycle Crunch", "core", "bodyweight", "beginner", true, false,
    ["Lie on your back, hands behind the head.",
     "Bring opposite elbow to opposite knee while extending the other leg.",
     "Alternate continuously."],
    "Rotate from the rib cage, not the elbows."),
  ex("Cable Wood Chop", "core", "cables", "intermediate", false, false,
    ["Stand sideways to a high cable, grip with both hands.",
     "Pull the handle diagonally across your body to the opposite hip.",
     "Return with control."],
    "Pivot the back foot for free hip rotation — protects the lower back."),
  ex("Kettlebell Windmill", "core", "kettlebell", "advanced", false, false,
    ["Press a kettlebell overhead.",
     "Hinge sideways toward the opposite foot, keeping the bell vertical.",
     "Return to standing."],
    "Move slowly — eyes stay on the bell the whole time."),
  ex("L-Sit", "core", "bodyweight", "advanced", true, true,
    ["Sit on two parallel surfaces (or parallettes), hands flat.",
     "Press your body up, legs out straight in front.",
     "Hold for prescribed time."],
    "Press the shoulders away from the ears to lock the position."),

  /* ============================ QUADS ============================ */
  ex("Back Squat", "quads", "barbell", "advanced", false, false,
    ["Bar across your upper back, feet shoulder-width.",
     "Brace and descend with knees tracking over toes until parallel.",
     "Drive through mid-foot to stand up."],
    "Keep your chest proud; let the hips and knees bend together."),
  ex("Front Squat", "quads", "barbell", "advanced", false, false,
    ["Bar across the front of the shoulders, elbows high.",
     "Squat down with an upright torso.",
     "Drive up keeping elbows high."],
    "Elbows up = quads up. Drop them and the bar will dump."),
  ex("Goblet Squat", "quads", "dumbbells", "beginner", false, false,
    ["Hold a dumbbell at chest height with both hands.",
     "Squat down until elbows reach inside knees.",
     "Drive up through the heels."],
    "Best entry point to learn squat depth."),
  ex("Bulgarian Split Squat", "quads", "dumbbells", "intermediate", false, false,
    ["Rear foot elevated on a bench, front foot ~2 ft forward.",
     "Lower into a deep lunge with the back knee tracking down.",
     "Drive up through the front heel."],
    "Front heel stays planted — drives the quad through full range."),
  ex("Bodyweight Squat", "quads", "bodyweight", "beginner", true, false,
    ["Stand with feet shoulder-width, toes slightly out.",
     "Hinge hips back and descend to parallel.",
     "Drive up through the heels to stand."],
    "Arms forward as a counter-balance — keep the chest tall."),
  ex("Jump Squat", "quads", "bodyweight", "intermediate", true, false,
    ["Stand feet shoulder-width.",
     "Squat down then explode up into a jump.",
     "Land softly through the mid-foot and reset."],
    "Quality over quantity — power, not endurance."),
  ex("Pistol Squat", "quads", "bodyweight", "advanced", true, false,
    ["Stand on one foot, the other extended in front.",
     "Squat down on the standing leg with the free leg straight out.",
     "Drive back up through the heel."],
    "Hold a counterweight (like a 2.5kg plate) to balance until strong enough."),
  ex("Leg Press", "quads", "machines", "beginner", false, false,
    ["Sit in the machine with feet shoulder-width on the platform.",
     "Lower until knees reach 90°.",
     "Press back without locking the knees."],
    "Don't let your lower back roll off the pad — adjust the seat instead."),
  ex("Hack Squat", "quads", "machines", "intermediate", false, false,
    ["Stand inside the hack squat with shoulders on the pads.",
     "Lower until knees track over toes.",
     "Drive up through the heels."],
    "Place feet slightly higher on the platform for extra quad focus."),
  ex("Leg Extension", "quads", "machines", "beginner", false, false,
    ["Sit in the machine with shins behind the pad.",
     "Extend the legs until they're straight.",
     "Lower under control."],
    "Pause and squeeze at the top — quads will burn fast."),
  ex("Wall Sit", "quads", "bodyweight", "beginner", true, true,
    ["Lean against a wall and slide down to a 90° knee bend.",
     "Knees over ankles, back flat against the wall.",
     "Hold for prescribed time."],
    "Press your lower back firmly into the wall the whole hold."),
  ex("Sissy Squat", "quads", "bodyweight", "advanced", true, false,
    ["Stand with feet shoulder-width, holding something light for balance.",
     "Lean back as you bend the knees, heels lifting.",
     "Squeeze the quads to return to upright."],
    "Knees travel forward over the toes — straight up the quad torture path."),
  ex("Step-Up", "quads", "dumbbells", "beginner", false, false,
    ["Stand in front of a box, dumbbells in hand.",
     "Step up and drive through the front heel to stand on the box.",
     "Step back down with control."],
    "Don't push off the floor with the bottom leg — let the top leg do everything."),
  ex("Banded Squat", "quads", "resistance bands", "beginner", false, false,
    ["Stand on a band, loop the other end over your shoulders.",
     "Squat down to parallel.",
     "Drive up to stand."],
    "Tension peaks at the top — perfect for travel."),

  /* ============================ HAMSTRINGS ============================ */
  ex("Romanian Deadlift", "hamstrings", "barbell", "intermediate", false, false,
    ["Hold the bar at hip level with a soft knee bend.",
     "Hinge at hips, lowering the bar to mid-shin while keeping back flat.",
     "Drive hips forward to stand back up."],
    "Push your hips back, not your knees forward."),
  ex("Stiff-Leg Deadlift", "hamstrings", "barbell", "intermediate", false, false,
    ["Stand with the bar at your shins, knees nearly straight.",
     "Hinge with a flat back, grip the bar.",
     "Stand up by squeezing glutes and hams."],
    "Big stretch but easy to round the back — start light."),
  ex("Single-Leg RDL", "hamstrings", "dumbbells", "intermediate", false, false,
    ["Hold a dumbbell in one hand, opposite foot lifts as you hinge.",
     "Lower the dumbbell toward the floor as the back leg rises.",
     "Return to standing with a strong glute squeeze."],
    "Imagine your body is a teeter-totter — head and back foot move together."),
  ex("Lying Leg Curl", "hamstrings", "machines", "beginner", false, false,
    ["Lie face down with the pad against your heels.",
     "Curl the heels toward your glutes.",
     "Lower with a 2-second negative."],
    "Pause and squeeze at the top — quality over speed."),
  ex("Seated Leg Curl", "hamstrings", "machines", "beginner", false, false,
    ["Sit with shins behind the pad.",
     "Curl the legs down toward the floor.",
     "Release slowly."],
    "Drive your knees down into the pad to maximise contraction."),
  ex("Nordic Curl", "hamstrings", "bodyweight", "advanced", true, false,
    ["Kneel with feet anchored under a bar or partner.",
     "Lower your body forward with a flat back, resisting the descent.",
     "Push back up using as much hamstring as possible."],
    "Brutally hard — start with assisted reps using a band overhead."),
  ex("Glute-Ham Raise", "hamstrings", "machines", "advanced", false, false,
    ["Lock in to a GHR machine with knees on the pad.",
     "Lower your torso forward with a flat back.",
     "Squeeze the hamstrings and glutes to rise back up."],
    "Bend at the knees, not the waist — keep a straight line."),
  ex("Kettlebell Swing", "hamstrings", "kettlebell", "intermediate", false, false,
    ["Hinge with the kettlebell between your legs.",
     "Drive hips forward to swing the bell to chest height.",
     "Let it fall back into the hinge."],
    "Hip drive — not arm raise. The arms just follow."),
  ex("Banded Leg Curl", "hamstrings", "resistance bands", "beginner", false, false,
    ["Anchor a band low; loop it around one ankle.",
     "Lie down, curl the heel to the glute.",
     "Lower with control."],
    "Excellent for hamstring activation pre-squats."),
  ex("Hip Thrust", "hamstrings", "barbell", "intermediate", false, false,
    ["Upper back on a bench, bar across the hips.",
     "Drive hips up until torso is parallel to the floor.",
     "Lower under control."],
    "Squeeze glutes hard at the top — pelvis stays neutral."),

  /* ============================ GLUTES ============================ */
  ex("Glute Bridge", "glutes", "bodyweight", "beginner", true, false,
    ["Lie on your back, feet flat, knees bent.",
     "Drive through your heels to lift hips until knees–hips–shoulders are in line.",
     "Squeeze glutes at the top, lower with control."],
    "Tuck your pelvis slightly to keep tension in the glutes."),
  ex("Walking Lunge", "glutes", "bodyweight", "beginner", true, false,
    ["Step forward into a lunge — front knee over ankle.",
     "Drive through the front heel to step the back foot through.",
     "Alternate sides."],
    "Keep an upright torso and a long stride for glute emphasis."),
  ex("Reverse Lunge", "glutes", "dumbbells", "beginner", false, false,
    ["Stand with dumbbells at your sides.",
     "Step one foot back into a lunge.",
     "Drive through the front heel to return."],
    "Easier on the knees than forward lunges — perfect for high reps."),
  ex("Curtsy Lunge", "glutes", "dumbbells", "intermediate", false, false,
    ["Stand with feet hip-width, dumbbells at your sides.",
     "Step one foot diagonally behind the other into a curtsy.",
     "Drive up to return."],
    "Hits the glute medius — adds shape to the hips."),
  ex("Cable Kickback", "glutes", "cables", "beginner", false, false,
    ["Strap to a low cable, face the machine.",
     "Kick one leg straight back without arching the back.",
     "Return slowly."],
    "Squeeze at the top — focus on the working glute, not the leg."),
  ex("Hip Thrust (BB)", "glutes", "barbell", "intermediate", false, false,
    ["Upper back on a bench, bar across the hips with a pad.",
     "Drive hips up until torso is parallel.",
     "Lower with control."],
    "Knees, hips, shoulders in a straight line at the top — no overarching."),
  ex("Single-Leg Hip Thrust", "glutes", "bodyweight", "intermediate", true, false,
    ["Upper back on a bench, one foot on the floor, the other lifted.",
     "Drive the planted foot's hip up.",
     "Lower slowly."],
    "Great unilateral fix for glute imbalances."),
  ex("Frog Pump", "glutes", "bodyweight", "beginner", true, false,
    ["Lie on your back, soles of feet together, knees out.",
     "Drive hips up by squeezing glutes.",
     "Lower with control."],
    "Brutal finisher — go for high reps to feel the burn."),
  ex("Sumo Deadlift", "glutes", "barbell", "advanced", false, false,
    ["Wide stance with toes turned out, bar over mid-foot.",
     "Grip inside the knees with a flat back.",
     "Drive through the floor to stand."],
    "Push your knees out the whole pull — opens the hips."),
  ex("Banded Lateral Walk", "glutes", "resistance bands", "beginner", false, false,
    ["Loop a band above the knees, partial squat.",
     "Step sideways with tension in the band.",
     "Return without letting the band slack."],
    "Slow, controlled — feel the glute medius fire each step."),
  ex("Step-Up to Knee Drive", "glutes", "bodyweight", "beginner", true, false,
    ["Step up onto a box with one foot.",
     "Drive the back knee up to hip height as you stand on the box.",
     "Lower under control."],
    "Drive the knee high — combines strength with athletic carryover."),
  ex("Donkey Kick", "glutes", "bodyweight", "beginner", true, false,
    ["Start on hands and knees, neutral spine.",
     "Drive one heel up to the ceiling, knee bent.",
     "Lower with control."],
    "Squeeze the glute — don't just lift the leg."),

  /* ============================ CALVES ============================ */
  ex("Standing Calf Raise", "calves", "bodyweight", "beginner", true, false,
    ["Stand on the edge of a step, heels hanging off.",
     "Lift onto the balls of your feet as high as possible.",
     "Lower slowly until heels are below the step."],
    "Pause for one second at the top for max contraction."),
  ex("Seated Calf Raise", "calves", "machines", "beginner", false, false,
    ["Sit with knees under the pad, balls of feet on the platform.",
     "Drive the heels up.",
     "Lower with a slow negative."],
    "Hits the soleus harder than standing variations."),
  ex("Donkey Calf Raise", "calves", "machines", "intermediate", false, false,
    ["Bend at the hips with the pad across your lower back.",
     "Drive up onto the balls of your feet.",
     "Lower fully."],
    "Bent-hip position gives a deeper stretch."),
  ex("Single-Leg Calf Raise", "calves", "bodyweight", "intermediate", true, false,
    ["Stand on one foot at the edge of a step.",
     "Lift onto the ball of the foot.",
     "Lower slowly past the step."],
    "Hold a dumbbell in the opposite hand to add load."),
  ex("Jump Rope", "calves", "bodyweight", "beginner", true, false,
    ["Hold a jump rope with relaxed shoulders.",
     "Bounce on the balls of your feet, rotating the wrists.",
     "Stay light and rhythmic."],
    "Two-foot bounces for skill — single-leg bounces for conditioning."),
  ex("Calf Raise on Leg Press", "calves", "machines", "beginner", false, false,
    ["Sit in a leg press with feet at the bottom of the platform, heels off.",
     "Press the platform with the balls of your feet.",
     "Return to a deep stretch."],
    "Don't unrack the safeties — keep them on for calf raises."),

  /* ============================ FULL BODY / COMPOUND ============================ */
  ex("Burpee", "full_body", "bodyweight", "intermediate", true, false,
    ["From standing, drop into a squat and place hands on the floor.",
     "Kick legs back to a plank, do a push-up.",
     "Jump feet to your hands and explode up into a jump."],
    "Pace yourself — quality reps over rushed sloppy ones."),
  ex("Thruster", "full_body", "dumbbells", "intermediate", false, false,
    ["Hold dumbbells at the shoulders, feet shoulder-width.",
     "Squat down to parallel.",
     "Drive up and press the dumbbells overhead in one motion."],
    "Use leg drive to launch the press — efficient power transfer."),
  ex("Clean", "full_body", "barbell", "advanced", false, false,
    ["Bar on the floor, hip-width grip.",
     "Pull the bar explosively, transitioning to a front rack.",
     "Stand tall, then reset."],
    "Slow pull off the floor — fast turnover at the top."),
  ex("Clean and Press", "full_body", "barbell", "advanced", false, false,
    ["Clean the bar to the front rack.",
     "Press it overhead in one motion.",
     "Return under control."],
    "Build the clean before adding the press — technique first."),
  ex("Snatch", "full_body", "barbell", "advanced", false, false,
    ["Wide grip on the bar, hip hinge.",
     "Pull the bar overhead in one explosive motion, catching it in a deep squat.",
     "Stand to lockout."],
    "Coach this — single-rep mistakes break wrists. Start with the PVC."),
  ex("Turkish Get-Up", "full_body", "kettlebell", "advanced", false, false,
    ["Lie on your back, kettlebell pressed overhead in one hand.",
     "Slowly rise to standing without breaking the press.",
     "Reverse the steps back to lying."],
    "Take 30 seconds per rep — every position is a hold."),
  ex("Man-Maker", "full_body", "dumbbells", "advanced", false, false,
    ["Dumbbells in hands, drop to a push-up position.",
     "Push-up, then row each dumbbell.",
     "Stand and thruster the dumbbells overhead."],
    "Brutal — pace yourself and stay near the dumbbells the whole time."),
  ex("Sled Push", "full_body", "machines", "intermediate", false, false,
    ["Load the sled, brace against the handles.",
     "Drive forward with a flat back, hard exhale each step.",
     "Walk back as recovery."],
    "Long arms = high handles, drive from the hips."),
  ex("Devil's Press", "full_body", "dumbbells", "advanced", false, false,
    ["Dumbbells on the floor, drop into a burpee.",
     "Swing the dumbbells up overhead in one motion.",
     "Reset and repeat."],
    "Brutal combo of burpee + dumbbell snatch."),
  ex("Kettlebell Clean", "full_body", "kettlebell", "intermediate", false, false,
    ["Start with the kettlebell between your feet.",
     "Hike it back, then snap your hips to clean to the rack.",
     "Lower under control."],
    "The bell should land softly on the forearm — no banging."),
  ex("Bear Crawl", "full_body", "bodyweight", "beginner", true, false,
    ["Hands and feet on the floor, knees hovering.",
     "Crawl forward by moving opposite arm and leg together.",
     "Maintain a flat back the entire time."],
    "Knees stay 1-2 inches off the floor — never touch down."),
  ex("Squat to Press", "full_body", "kettlebell", "intermediate", false, false,
    ["Hold a kettlebell in the goblet position.",
     "Squat to parallel.",
     "Stand and press the bell overhead."],
    "Press as you stand — momentum transfer makes it efficient."),

  /* ============================ CARDIO / CONDITIONING ============================ */
  ex("Jumping Jacks", "cardio", "bodyweight", "beginner", true, false,
    ["Stand with feet together, arms at your sides.",
     "Jump feet out while raising arms overhead.",
     "Reverse the motion."],
    "Stay light on the balls of your feet."),
  ex("High Knees", "cardio", "bodyweight", "beginner", true, false,
    ["Stand tall.",
     "Drive one knee up to hip height, then the other, as fast as possible.",
     "Pump the arms."],
    "Land light — quiet feet means good control."),
  ex("Box Jump", "cardio", "bodyweight", "intermediate", true, false,
    ["Stand in front of a sturdy box.",
     "Load up with a quarter-squat.",
     "Jump onto the box and stand tall, then step down."],
    "Step down — jumping down repeatedly is hard on the knees."),
  ex("Skater Jump", "cardio", "bodyweight", "intermediate", true, false,
    ["Start on one foot.",
     "Leap sideways to land on the other foot, free leg trailing behind.",
     "Reverse direction."],
    "Long, controlled lateral hops — feel the glute medius work."),
  ex("Rowing Machine", "cardio", "machines", "beginner", false, false,
    ["Strap in, legs bent, arms extended.",
     "Drive with the legs, then pull with the arms.",
     "Reverse: arms, body, legs."],
    "Legs power, arms finish — 60/40 effort split."),
  ex("Assault Bike", "cardio", "machines", "intermediate", false, false,
    ["Sit on the bike, grip the moving handles.",
     "Drive arms and legs simultaneously.",
     "Maintain steady output."],
    "Brutal cardio when you push hard — pace makes everything."),
  ex("Stairmaster", "cardio", "machines", "beginner", false, false,
    ["Step onto the stairmaster at a slow, sustainable pace.",
     "Use a tall posture and avoid leaning on the rails.",
     "Climb continuously for prescribed time."],
    "Add weight in a vest for an intense glute-and-hams session."),
  ex("Treadmill Sprint", "cardio", "machines", "intermediate", false, false,
    ["Set the treadmill to a sprint pace.",
     "Sprint for prescribed time (start 15s).",
     "Recover by stepping to the side rails."],
    "Hit the side rails to recover safely — don't ride the belt."),
  ex("Battle Ropes", "cardio", "machines", "intermediate", false, false,
    ["Hold the ends of the ropes with a soft squat.",
     "Whip the ropes in waves for the prescribed time.",
     "Vary between alternating and double waves."],
    "Drive the wave from the hips and core, not just the arms."),
  ex("Shuttle Run", "cardio", "bodyweight", "intermediate", true, false,
    ["Mark two cones 10–20m apart.",
     "Sprint between them, touching each line.",
     "Rest and repeat."],
    "Drop your hips low to decelerate — knees thank you."),
];

export const muscleGroupLabels = {
  chest: "Chest",
  back: "Back",
  shoulders: "Shoulders",
  biceps: "Biceps",
  triceps: "Triceps",
  forearms: "Forearms",
  core: "Core",
  quads: "Quads",
  hamstrings: "Hamstrings",
  glutes: "Glutes",
  calves: "Calves",
  full_body: "Full body",
  cardio: "Cardio",
};

export const equipmentLabels = {
  barbell: "Barbell",
  dumbbells: "Dumbbells",
  kettlebell: "Kettlebell",
  machines: "Machines",
  cables: "Cables",
  "resistance bands": "Resistance bands",
  "pull-up bar": "Pull-up bar",
  bodyweight: "Bodyweight",
};

export const difficultyLabels = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};
